/**
 * T205-FE: the one rule that says which file the Home paints behind the
 * category chips, shared by every place that has to name it before the app
 * runs (the Vite plugin for the static index.html).
 *
 * `HomePage` writes `--hero-map-image` with `brandBackgroundImage('/hero-map.jpg')`
 * (buzzmap-web/app/src/utils/brandImages.ts), which takes the LARGEST variant of
 * the original in `responsive-assets.json` at every viewport; the CSS has no
 * media query or image-set for it. This module applies the same rule to the same
 * JSON, so the preloaded URL is, by construction, the one the CSS requests.
 *
 * Where the image is painted: ONLY by `HomePageMobile`. `HomePage` renders `HomeDesktop`
 * (no hero map) when `useIsDesktop()` is true, so a preload without a media condition would
 * download 53 KB that a desktop viewport never uses. The condition is the complement of the
 * query `useIsDesktop` evaluates, taken from the same constant (`DESKTOP_MIN_WIDTH_QUERY`).
 *
 * Pure and import-free: Node runs it directly from the Vite plugin (and the web app bundles it).
 */

export const HERO_MAP_ORIGINAL = '/hero-map.jpg';

/** Viewport query that switches HomePage to its desktop layout; `useIsDesktop` evaluates exactly this. */
export const DESKTOP_MIN_WIDTH_QUERY = '(min-width: 1024px)';

/** Viewports where HomePageMobile (the only painter of the hero map) is mounted: not desktop. */
export const HERO_MAP_PRELOAD_MEDIA = `not all and ${DESKTOP_MIN_WIDTH_QUERY}`;

interface GeneratedAssetsLike {
  assets?: Record<string, { variants?: Array<{ path?: unknown }> } | undefined>;
}

/** Path of the largest variant of `original` in the generated manifest, or undefined. */
export function largestVariantPath(generated: unknown, original: string): string | undefined {
  const assets = (generated as GeneratedAssetsLike | null | undefined)?.assets;
  if (!assets || typeof assets !== 'object' || !Object.hasOwn(assets, original)) return undefined;
  const variants = assets[original]?.variants;
  if (!Array.isArray(variants)) return undefined;
  const path = variants[variants.length - 1]?.path;
  return typeof path === 'string' ? path : undefined;
}

/**
 * Same-origin asset paths only (letters, digits, `._-`, slashes; not protocol-relative, no `..`):
 * safe to write into an attribute as is.
 */
const SAFE_ASSET_PATH = /^\/(?!\/)(?!.*\.\.)[A-Za-z0-9._/-]+$/;

/** URL the Home CSS requests for the hero map, or undefined when the manifest does not know it. */
export function heroMapUrl(generated: unknown): string | undefined {
  const path = largestVariantPath(generated, HERO_MAP_ORIGINAL);
  return path !== undefined && SAFE_ASSET_PATH.test(path) ? path : undefined;
}

export interface HeroMapPreloadAttributes {
  rel: 'preload';
  as: 'image';
  href: string;
  media: string;
  fetchpriority: 'high';
}

/**
 * Attributes of the preload: `media` limits it to the layout that paints the image. No
 * `imagesrcset`/`imagesizes` (the CSS asks for one URL at every viewport where it asks at all)
 * and no `crossorigin` (a CSS background image is fetched in no-cors mode, so a plain image
 * preload is the entry the browser reuses).
 */
export function heroMapPreloadAttributes(url: string): HeroMapPreloadAttributes {
  return {
    rel: 'preload',
    as: 'image',
    href: url,
    media: HERO_MAP_PRELOAD_MEDIA,
    fetchpriority: 'high',
  };
}
